import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import AttributeDomain, {
  AttributeType,
} from "entities/domain/attributes/attribute-domain";
import TagsDomain from "entities/domain/tags/tags-domain";
import { VehicleMakeAndModel } from "entities/domain/vehicle";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import { useAppSelector } from "redux/hooks";
import AttributeService, {
  CreateAttributePayload,
  UpdateAttributePayload,
} from "services/attributes";
import CustomFieldService, {
  CustomFieldDomainKey,
} from "services/customFields";
import MerchantService from "services/merchant";
import TagsService from "services/tags";
import { colorList } from "util/methods";
import { removeById, upsertById } from "./query-cache-helpers";

// Query keys
export const attributesQueryKey = (groupId: string | null | undefined) => [
  "attributes",
  groupId ?? null,
];

export const tagsQueryKey = (groupId: string | null | undefined) => [
  "tags",
  groupId ?? null,
];

export const makesAndModelsQueryKey = (groupId: string | null | undefined) => [
  "makes-and-models",
  groupId ?? null,
];

export const salesOrderTypesQueryKey = (groupId: string | null | undefined) => [
  "sales-order-types",
  groupId ?? null,
];

export const customFieldKeysQueryKey = (groupId: string | null | undefined) => [
  "custom-field-keys",
  groupId ?? null,
];

// Helper to sort attributes by created_at
const sortAttributes = (a: AttributeDomain, b: AttributeDomain) =>
  new Date(a.created_at).getTime() - new Date(b.created_at).getTime();

// Helper to transform makesAndModels array to object format
const transformMakesAndModels = (
  makesAndModels: VehicleMakeAndModel[]
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  makesAndModels.forEach((makeAndModel) => {
    if (!result[makeAndModel.name]) {
      result[makeAndModel.name] = [];
    }
    result[makeAndModel.name].push(...makeAndModel.models);
  });
  return result;
};

/**
 * Hook to fetch attributes for a group
 */
export function useAttributes(staleTimeMs: number = 60_000) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();

  return useQuery<AttributeDomain[], Error>({
    queryKey: attributesQueryKey(merchant?.groupId),
    enabled: Boolean(merchant?.groupId),
    queryFn: async () => {
      if (!merchant?.groupId) return [];
      const attributes = await AttributeService.getAttributesForGroup(
        auth0Context,
        merchant.groupId
      );
      return attributes.sort(sortAttributes);
    },
    staleTime: staleTimeMs,
  });
}

/**
 * Hook to create an attribute
 * Optimistically updates the cache
 */
export function useCreateAttribute() {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const queryClient = useQueryClient();

  return useMutation<
    AttributeDomain,
    Error,
    CreateAttributePayload,
    { prevAttributes?: AttributeDomain[] }
  >({
    mutationFn: async (payload) => {
      if (!merchant?.groupId) {
        throw new Error("Merchant group ID is required");
      }
      return AttributeService.createAttribute(
        auth0Context,
        merchant.groupId,
        payload
      );
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: attributesQueryKey(merchant?.groupId),
      });

      const prevAttributes = queryClient.getQueryData<AttributeDomain[]>(
        attributesQueryKey(merchant?.groupId)
      );

      // Optimistically add attribute (will be sorted on success)
      if (prevAttributes && merchant?.groupId) {
        const optimisticAttribute = new AttributeDomain(
          `temp-${Date.now()}`,
          payload.name,
          payload.type,
          merchant.groupId,
          new Date().toISOString()
        );
        queryClient.setQueryData<AttributeDomain[]>(
          attributesQueryKey(merchant?.groupId),
          [optimisticAttribute, ...prevAttributes]
        );
      }

      return { prevAttributes };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevAttributes && merchant?.groupId) {
        queryClient.setQueryData(
          attributesQueryKey(merchant.groupId),
          context.prevAttributes
        );
      }
    },
    onSuccess: (created) => {
      if (!merchant?.groupId) return;
      queryClient.setQueryData<AttributeDomain[]>(
        attributesQueryKey(merchant.groupId),
        (old) => {
          if (!old) return [created];
          // Remove temp attribute if exists, then add real one and sort
          const filtered = old.filter((a) => !a.id.startsWith("temp-"));
          return [created, ...filtered].sort(sortAttributes);
        }
      );
    },
  });
}

/**
 * Hook to update an attribute
 * Optimistically updates the cache
 */
export function useUpdateAttribute() {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const queryClient = useQueryClient();

  return useMutation<
    AttributeDomain,
    Error,
    { id: string; payload: UpdateAttributePayload },
    { prevAttributes?: AttributeDomain[] }
  >({
    mutationFn: async ({ id, payload }) => {
      if (!merchant?.groupId) {
        throw new Error("Merchant group ID is required");
      }
      return AttributeService.updateAttribute(
        auth0Context,
        merchant.groupId,
        id,
        payload
      );
    },
    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries({
        queryKey: attributesQueryKey(merchant?.groupId),
      });

      const prevAttributes = queryClient.getQueryData<AttributeDomain[]>(
        attributesQueryKey(merchant?.groupId)
      );

      // Optimistically update attribute
      if (prevAttributes && merchant?.groupId) {
        queryClient.setQueryData<AttributeDomain[]>(
          attributesQueryKey(merchant?.groupId),
          prevAttributes.map((a) =>
            a.id === id
              ? new AttributeDomain(
                  a.id,
                  payload.name,
                  a.type,
                  a.group_id,
                  a.created_at.toISOString()
                )
              : a
          )
        );
      }

      return { prevAttributes };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevAttributes && merchant?.groupId) {
        queryClient.setQueryData(
          attributesQueryKey(merchant.groupId),
          context.prevAttributes
        );
      }
    },
    onSuccess: (updated) => {
      if (!merchant?.groupId) return;
      queryClient.setQueryData<AttributeDomain[]>(
        attributesQueryKey(merchant.groupId),
        (old) => {
          if (!old) return [updated];
          return upsertById(old, updated).sort(sortAttributes);
        }
      );
    },
  });
}

/**
 * Hook to delete an attribute
 * Optimistically removes from cache
 */
export function useDeleteAttribute() {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { id: string },
    { prevAttributes?: AttributeDomain[] }
  >({
    mutationFn: async ({ id }) => {
      if (!merchant?.groupId) {
        throw new Error("Merchant group ID is required");
      }
      await AttributeService.deleteAttribute(
        auth0Context,
        merchant.groupId,
        id
      );
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({
        queryKey: attributesQueryKey(merchant?.groupId),
      });

      const prevAttributes = queryClient.getQueryData<AttributeDomain[]>(
        attributesQueryKey(merchant?.groupId)
      );

      // Optimistically remove attribute
      if (prevAttributes) {
        queryClient.setQueryData<AttributeDomain[]>(
          attributesQueryKey(merchant?.groupId),
          prevAttributes.filter((a) => a.id !== id)
        );
      }

      return { prevAttributes };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevAttributes && merchant?.groupId) {
        queryClient.setQueryData(
          attributesQueryKey(merchant.groupId),
          context.prevAttributes
        );
      }
    },
    onSuccess: (_data, { id }) => {
      if (!merchant?.groupId) return;
      // Ensure it's removed (in case optimistic update didn't catch it)
      queryClient.setQueryData<AttributeDomain[]>(
        attributesQueryKey(merchant.groupId),
        (old) => old?.filter((a) => a.id !== id) ?? []
      );
    },
  });
}

/**
 * Hook to fetch tags for a group
 */
export function useTags(staleTimeMs: number = 60_000) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();

  return useQuery<TagsDomain[], Error>({
    queryKey: tagsQueryKey(merchant?.groupId),
    enabled: Boolean(merchant?.groupId),
    queryFn: async () => {
      if (!merchant?.groupId) return [];
      return TagsService.getTags(auth0Context, merchant.groupId);
    },
    staleTime: staleTimeMs,
  });
}

/**
 * Hook to add tags
 * Optimistically updates the cache
 */
export function useAddTags() {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const queryClient = useQueryClient();

  return useMutation<
    TagsDomain[],
    Error,
    string[],
    { prevTags?: TagsDomain[] }
  >({
    mutationFn: async (tagNames) => {
      if (!merchant?.groupId) {
        throw new Error("Merchant group ID is required");
      }
      return TagsService.addTags(auth0Context, merchant.groupId, tagNames);
    },
    onMutate: async (tagNames) => {
      await queryClient.cancelQueries({
        queryKey: tagsQueryKey(merchant?.groupId),
      });

      const prevTags = queryClient.getQueryData<TagsDomain[]>(
        tagsQueryKey(merchant?.groupId)
      );

      // Optimistically add tags with colors
      if (prevTags) {
        const newTags = tagNames.map((name, index) => {
          const color = colorList[(prevTags.length + index) % colorList.length];
          return new TagsDomain(name, color, `temp-${Date.now()}-${index}`);
        });
        queryClient.setQueryData<TagsDomain[]>(
          tagsQueryKey(merchant?.groupId),
          [...prevTags, ...newTags]
        );
      }

      return { prevTags };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevTags && merchant?.groupId) {
        queryClient.setQueryData(
          tagsQueryKey(merchant.groupId),
          context.prevTags
        );
      }
    },
    onSuccess: (createdTags) => {
      if (!merchant?.groupId) return;
      queryClient.setQueryData<TagsDomain[]>(
        tagsQueryKey(merchant.groupId),
        (old) => {
          if (!old) return createdTags;
          // Remove temp tags and add real ones
          const filtered = old.filter((t) => !t.id.startsWith("temp-"));
          // Merge with created tags, avoiding duplicates
          const existingIds = new Set(filtered.map((t) => t.id));
          const newTags = createdTags.filter((t) => !existingIds.has(t.id));
          return [...filtered, ...newTags];
        }
      );
    },
  });
}

/**
 * Helper function to remove a tag from cache
 * Used by WebSocket handlers when tag is deleted via socket event
 */
export function removeTagFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  groupId: string,
  tagId: string
) {
  queryClient.setQueryData<TagsDomain[]>(tagsQueryKey(groupId), (old) =>
    old ? old.filter((t) => t.id !== tagId) : []
  );
}

/**
 * Hook to delete a tag
 * Optimistically removes from cache
 */
export function useDeleteTag() {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string }, { prevTags?: TagsDomain[] }>({
    mutationFn: async ({ id }) => {
      if (!merchant?.groupId) {
        throw new Error("Merchant group ID is required");
      }
      await TagsService.deleteTag(auth0Context, merchant.groupId, id);
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({
        queryKey: tagsQueryKey(merchant?.groupId),
      });

      const prevTags = queryClient.getQueryData<TagsDomain[]>(
        tagsQueryKey(merchant?.groupId)
      );

      if (prevTags) {
        queryClient.setQueryData<TagsDomain[]>(
          tagsQueryKey(merchant?.groupId),
          prevTags.filter((t) => t.id !== id)
        );
      }

      return { prevTags };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevTags && merchant?.groupId) {
        queryClient.setQueryData(
          tagsQueryKey(merchant.groupId),
          context.prevTags
        );
      }
    },
  });
}

/**
 * Hook to batch delete tags
 * Optimistically removes from cache
 */
export function useDeleteTags() {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { ids: string[] },
    { prevTags?: TagsDomain[] }
  >({
    mutationFn: async ({ ids }) => {
      if (!merchant?.groupId) {
        throw new Error("Merchant group ID is required");
      }
      await TagsService.deleteTags(auth0Context, merchant.groupId, ids);
    },
    onMutate: async ({ ids }) => {
      await queryClient.cancelQueries({
        queryKey: tagsQueryKey(merchant?.groupId),
      });

      const prevTags = queryClient.getQueryData<TagsDomain[]>(
        tagsQueryKey(merchant?.groupId)
      );

      if (prevTags) {
        const idsSet = new Set(ids);
        queryClient.setQueryData<TagsDomain[]>(
          tagsQueryKey(merchant?.groupId),
          prevTags.filter((t) => !idsSet.has(t.id))
        );
      }

      return { prevTags };
    },
    onError: (_err, _vars, context) => {
      if (context?.prevTags && merchant?.groupId) {
        queryClient.setQueryData(
          tagsQueryKey(merchant.groupId),
          context.prevTags
        );
      }
    },
  });
}

/**
 * Hook to fetch makes and models for a group
 */
export function useMakesAndModels(staleTimeMs: number = 60_000) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();

  return useQuery<Record<string, string[]>, Error>({
    queryKey: makesAndModelsQueryKey(merchant?.groupId),
    enabled: Boolean(merchant?.groupId),
    queryFn: async () => {
      if (!merchant?.groupId) return {};
      const makesAndModels =
        await MerchantService.getMerchantVehicleMakesAndModels(
          auth0Context,
          merchant.groupId
        );
      return transformMakesAndModels(makesAndModels);
    },
    staleTime: staleTimeMs,
  });
}

/**
 * Hook to fetch the sales-order-type values this group's vehicles hold.
 *
 * The column is an open set, so the options come from the group's own data
 * rather than a hard-coded list.
 */
export function useSalesOrderTypes(staleTimeMs: number = 60_000) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();

  return useQuery<string[], Error>({
    queryKey: salesOrderTypesQueryKey(merchant?.groupId),
    enabled: Boolean(merchant?.groupId),
    queryFn: async () => {
      if (!merchant?.groupId) return [];
      return MerchantService.getMerchantVehicleSalesOrderTypes(
        auth0Context,
        merchant.groupId
      );
    },
    staleTime: staleTimeMs,
  });
}

/**
 * Hook to fetch custom field keys for a group
 */
export function useCustomFieldKeys(staleTimeMs: number = 60_000) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();

  return useQuery<Partial<Record<CustomFieldDomainKey, string[]>>, Error>({
    queryKey: customFieldKeysQueryKey(merchant?.groupId),
    enabled: Boolean(merchant?.groupId),
    queryFn: async () => {
      if (!merchant?.groupId) return {};
      return CustomFieldService.getCustomFieldsForGroup(
        auth0Context,
        merchant.groupId
      );
    },
    staleTime: staleTimeMs,
  });
}
